import { Deferred, Head, router, useForm } from "@inertiajs/react"
import { ArrowRight } from "@untitledui/icons"
import { format } from "date-fns"
import { useEffect, useState } from "react"

import { PageHeader } from "@/components/console/page-header"
import { SchoolLink } from "@/components/console/school-link"
import type { GroundSubject } from "@/components/ground/store"
import { useGround } from "@/components/ground/use-ground"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cx } from "@/components/ui/cx"
import { Notice } from "@/components/ui/notice"
import { Field, Fields, Panel } from "@/components/ui/panel"
import { StatusDot } from "@/components/ui/status-dot"
import { useClipboard } from "@/hooks/use-clipboard"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminSalesforceJobsPath,
  prepareCheckinAdminSalesforceJobPath,
} from "@/routes"
import type { SchoolReference } from "@/types"

import type { JobStatusColor } from "./types"

interface Job {
  id: number
  salesforce_id: string
  name: string | null
  program_name: string | null
  /** The account as a reference; null for an orphan job. */
  school: SchoolReference | null
  start_time: string | null
  finish_time: string | null
  duration: number | null
  status: string | null
  status_color: JobStatusColor
  job_type: string | null
  delivery_method: string | null
  year_level: string | null
  school_gender: string | null
  region: string | null
  expected_attendance: number | null
  actual_attendance: number | null
  postcode: string | null
  record_type_name: string | null
  synced_at: string | null
}

interface Account {
  id: number
  salesforce_id: string
  name: string | null
  account_type: string | null
  record_type_name: string | null
  billing_street: string | null
  billing_city: string | null
  billing_state: string | null
  billing_postal_code: string | null
  phone: string | null
  website: string | null
  school_suburb: string | null
}

type Record_ = Record<string, unknown>

interface Inspector {
  available: boolean
  allocations: Record_[]
  contact: Record_ | null
  location: Record_ | null
  surveys: Record_[]
  program_statistics: Record_[]
}

interface PreparedBooking {
  token: string
  form_url: string
  program_type: string | null
  program_date: string | null
  year_level: string | null
  contact_present: boolean
}

interface Props {
  job: Job
  account: Account | null
  /** Deferred: absent until the partial reload lands, so the sheet arrives before the rail answers. */
  inspector?: Inspector
  prepared_booking: PreparedBooking | null
  /** The job's mappable point for the ground; null when the mirror has no coordinates. */
  ground: GroundSubject | null
}

const stamp = (iso: string | null, pattern = "d MMM yyyy · HH:mm") =>
  iso ? format(new Date(iso), pattern) : "—"

const display = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  return String(value)
}

const humanize = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

/*
 * The Salesforce job screen — the Master Design System's applied reference
 * (section 08): crumb line with the sync stamp · header with the id in mono,
 * the account as the title, the status as a dot, the program as an eyebrow ·
 * sections on the sheet, the cohort drawn in the certainty grammar · the debug
 * well · a right rail of everything fetched on view. The ground resolves onto
 * the job's point (section 12): the sheet sits a band lower and the map shows
 * the school above it.
 */
export default function AdminSalesforceJobsShow({
  job,
  account,
  inspector,
  prepared_booking,
  ground,
}: Props) {
  useGround(ground ? { kind: "resolve", ...ground } : null)
  const settled = useInspectorSettled()

  const code = job.name ?? job.salesforce_id
  const meta = [
    stamp(job.start_time),
    job.delivery_method,
    job.duration ? `${job.duration} min` : null,
  ]
    .filter(Boolean)
    .join(" · ")
  const address = [
    account?.billing_street,
    account?.billing_city,
    account?.billing_state,
    account?.billing_postal_code,
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <AdminLayout>
      <Head title={`Salesforce Job · ${code}`} />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          size="lg"
          className="border-secondary border-b pb-5"
          crumbs={[
            { label: "salesforce", href: adminSalesforceJobsPath() },
            { label: "jobs", href: adminSalesforceJobsPath() },
            { label: code },
          ]}
          stamp={`mirror synced ${stamp(job.synced_at, "d MMM HH:mm")}`}
          code={code}
          title={
            <>
              <SchoolLink school={job.school} />
              {job.year_level && (
                <span className="text-tertiary font-normal">
                  {" "}
                  ({job.year_level})
                </span>
              )}
            </>
          }
          actions={
            job.program_name && (
              <span className="notation text-pretty md:max-w-xs md:text-right">
                {job.program_name}
              </span>
            )
          }
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {job.status && (
              <StatusDot tier={job.status_color}>
                {job.status.toLowerCase()}
              </StatusDot>
            )}
            {job.job_type && (
              <Badge type="modern" color="gray" size="sm">
                {job.job_type}
              </Badge>
            )}
            <span className="notation">{meta}</span>
          </div>
          {(address || account?.phone) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {address && <span className="notation">{address}</span>}
              {account?.phone && (
                <a
                  href={`tel:${account.phone}`}
                  className="notation text-brand-secondary hover:text-brand-secondary_hover"
                >
                  {account.phone}
                </a>
              )}
            </div>
          )}
        </PageHeader>

        {inspector && !inspector.available && (
          <Notice tone="caution" role="status">
            Live Salesforce data is unavailable — the rail on the right may be
            incomplete. The mirror at left is local truth.
          </Notice>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-10">
          <section className="flex min-w-0 flex-col gap-6">
            <CheckinAction job={job} prepared={prepared_booking} />

            <Panel title="Job" note={`sked__Job__c · ${job.salesforce_id}`}>
              <Cohort
                expected={job.expected_attendance}
                actual={job.actual_attendance}
              />
              <Fields>
                <Field label="Start">
                  <span className="font-mono text-xs tabular-nums">
                    {stamp(job.start_time)}
                  </span>
                </Field>
                <Field label="Finish">
                  <span className="font-mono text-xs tabular-nums">
                    {stamp(job.finish_time)}
                  </span>
                </Field>
                <Field label="Duration">
                  <span className="font-mono text-xs tabular-nums">
                    {job.duration ? `${job.duration} min` : "—"}
                  </span>
                </Field>
                <Field label="Delivery">{display(job.delivery_method)}</Field>
                <Field label="Year level">{display(job.year_level)}</Field>
                <Field label="Gender">{display(job.school_gender)}</Field>
                <Field label="Region">{display(job.region)}</Field>
                <Field label="Postcode">
                  <span className="font-mono text-xs tabular-nums">
                    {display(job.postcode)}
                  </span>
                </Field>
              </Fields>
            </Panel>

            <Panel
              title="Account"
              note={account ? account.salesforce_id : undefined}
            >
              {account ? (
                <Fields>
                  <Field label="Name">
                    <SchoolLink school={account} />
                  </Field>
                  <Field label="Type">{display(account.account_type)}</Field>
                  <Field label="Suburb">{display(account.school_suburb)}</Field>
                  <Field label="Billing">{address || "—"}</Field>
                  <Field label="Phone">
                    {account.phone ? (
                      <a
                        href={`tel:${account.phone}`}
                        className="text-brand-secondary hover:text-brand-secondary_hover font-mono text-xs tabular-nums"
                      >
                        {account.phone}
                      </a>
                    ) : (
                      "—"
                    )}
                  </Field>
                  <Field label="Website">
                    {account.website ? (
                      <a
                        href={account.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-secondary hover:text-brand-secondary_hover break-all"
                      >
                        {account.website}
                      </a>
                    ) : (
                      "—"
                    )}
                  </Field>
                </Fields>
              ) : (
                <p className="text-tertiary text-sm">
                  No linked account (orphan job).
                </p>
              )}
            </Panel>

            <DebugWell
              rows={[
                ["job name", `${job.name ?? "—"} (sked__Job__c)`],
                ["job id", job.salesforce_id],
                ["account id", account?.salesforce_id ?? "—"],
                ["record type", job.record_type_name ?? "—"],
                ["last sync", stamp(job.synced_at, "d MMM yyyy HH:mm:ss")],
                [
                  "live fetch",
                  inspector
                    ? inspector.available
                      ? `${inspector.allocations.length} allocations · ${inspector.surveys.length} surveys · ${inspector.program_statistics.length} stats`
                      : "unavailable"
                    : settled
                      ? "unavailable"
                      : "fetching…",
                ],
              ]}
            />
          </section>

          <aside
            aria-busy={!inspector && !settled}
            className="remote bg-primary flex h-fit flex-col overflow-hidden lg:sticky lg:top-8"
          >
            <div className="border-secondary flex items-center justify-between gap-2.5 border-b px-4 py-3">
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={cx(
                    "remote-dot",
                    !inspector && !settled && "animate-pulse",
                  )}
                />
                <span className="eyebrow text-primary">
                  Live from Salesforce
                </span>
              </span>
              <span className="notation" role="status">
                {inspector
                  ? inspector.available
                    ? "fetched on view"
                    : "unavailable"
                  : settled
                    ? "unavailable"
                    : "fetching"}
              </span>
            </div>
            {/* the rail is fetched after the sheet has arrived; until it lands the slots are dashed — the lifecycle grammar, never a spinner */}
            <Deferred
              data="inspector"
              fallback={<RailFetching failed={settled} />}
            >
              {inspector && (
                <div className="flex flex-col px-4 pb-3">
                  <RecordListSection
                    title="Allocations"
                    items={inspector.allocations}
                  />
                  <RecordSection title="Contact" record={inspector.contact} />
                  <RecordSection title="Location" record={inspector.location} />
                  <RecordListSection
                    title="Surveys"
                    items={inspector.surveys}
                  />
                  <RecordListSection
                    title="Program statistics"
                    items={inspector.program_statistics}
                  />
                  <p className="notation border-secondary border-t pt-3 text-pretty">
                    fetched on view — not stored · the mirror at left is local
                    truth
                  </p>
                </div>
              )}
            </Deferred>
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}

/*
 * The cohort, drawn in the certainty grammar: an expected number is an
 * estimate (dashed, in caution because it still needs a person); an actual
 * attendance is measured (a solid do stroke under the figure).
 */
function Cohort({
  expected,
  actual,
}: {
  expected: number | null
  actual: number | null
}) {
  const measured = actual !== null
  const figure = measured ? actual : expected
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="eyebrow w-full">Cohort</span>
      <span
        className={cx(
          "text-primary inline-block border-b-2 pb-1 font-mono text-[26px] leading-none font-semibold tracking-[-0.02em] tabular-nums",
          measured ? "border-do-500" : "border-caution-500 border-dashed",
        )}
      >
        {figure ?? "—"}
        <span className="text-quaternary text-2xs ml-1.5 font-mono font-normal tracking-normal">
          students
        </span>
      </span>
      <span className={cx("notation", !measured && "text-caution-700")}>
        {measured
          ? `attended${expected !== null ? ` · ${expected} expected` : ""}`
          : figure === null
            ? "no number yet"
            : "expected · unconfirmed"}
      </span>
    </div>
  )
}

function DebugWell({ rows }: { rows: [string, string][] }) {
  const [copiedText, copy] = useClipboard()
  const text = rows.map(([k, v]) => `${k}: ${v}`).join("\n")
  return (
    <div className="well flex flex-col overflow-hidden">
      <div className="border-secondary flex items-baseline justify-between gap-3 border-b px-4 py-2.5">
        <span className="eyebrow">Debug details</span>
        <button
          type="button"
          onClick={() => copy(text)}
          className="notation text-brand-secondary hover:text-brand-secondary_hover focus-visible:outline-focus-ring rounded-xs outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {copiedText === text ? "copied" : "copy all"}
        </button>
      </div>
      <dl className="text-secondary text-2xs grid grid-cols-[118px_1fr] gap-x-3.5 gap-y-1 px-4 py-3 font-mono leading-relaxed">
        {rows.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-quaternary">{key}</dt>
            <dd className="break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function CheckinAction({
  job,
  prepared,
}: {
  job: Job
  prepared: PreparedBooking | null
}) {
  const { post, processing } = useForm({})

  if (prepared) {
    return (
      <div className="card flex flex-col gap-3 px-5 py-4">
        <div className="flex items-center gap-2">
          <span aria-hidden="true" className="confirmed-dot" />
          <h2 className="text-md text-primary">Check-in prepared</h2>
        </div>
        <p className="text-tertiary text-sm">
          A local booking is materialised for this job. Open the tokenised
          check-in form the school contact will receive.
        </p>
        {!prepared.contact_present && (
          <p className="text-caution-700 text-sm">
            No school contact is linked yet — this job may be missing a
            Salesforce contact. Re-run once one is available to attach it.
          </p>
        )}
        <div>
          <Button
            href={prepared.form_url}
            color="primary"
            size="md"
            iconTrailing={ArrowRight}
          >
            Open check-in form
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="provisional flex flex-col gap-3 px-5 py-4">
      <h2 className="text-md text-primary">Prepare check-in</h2>
      <p className="text-tertiary text-sm">
        Materialise a local booking, cohort, and check-in from this job, pulling
        the school contact live from Salesforce. Idempotent — running it again
        re-opens the same booking.
      </p>
      <div>
        <Button
          color="primary"
          size="md"
          isDisabled={processing}
          isLoading={processing}
          onPress={() => post(prepareCheckinAdminSalesforceJobPath(job.id))}
        >
          Prepare check-in for this job
        </Button>
      </div>
    </div>
  )
}

/*
 * Whether a reload for the inspector has come back. The prop itself says
 * whether it came back WITH the inspector; `settled` without it is a fetch
 * that failed (a dropped connection, a redirect), which must read as
 * unavailable rather than fetching forever. A new reload — the prepare
 * check-in round trip leaves the prop absent again — starts the clock over.
 */
function useInspectorSettled() {
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    const forInspector = (only: string[]) => only.includes("inspector")
    const offStart = router.on("start", (event) => {
      if (forInspector(event.detail.visit.only)) setSettled(false)
    })
    const offFinish = router.on("finish", (event) => {
      if (forInspector(event.detail.visit.only)) setSettled(true)
    })
    return () => {
      offStart()
      offFinish()
    }
  }, [])
  return settled
}

const RAIL_SECTIONS = [
  "Allocations",
  "Contact",
  "Location",
  "Surveys",
  "Program statistics",
]

/** The rail before it answers: the same sections, each a provisional slot at its real width. */
function RailFetching({ failed }: { failed: boolean }) {
  return (
    <div className="flex flex-col px-4 pb-3">
      {RAIL_SECTIONS.map((title) => (
        <div
          key={title}
          className="border-tertiary flex flex-col gap-2 border-b py-3"
        >
          <h3 className="text-primary text-sm">{title}</h3>
          {failed ? (
            <p className="text-quaternary text-sm">Not available.</p>
          ) : (
            <span aria-hidden="true" className="provisional-slot h-5 w-2/3" />
          )}
        </div>
      ))}
      <p className="notation border-secondary border-t pt-3 text-pretty">
        {failed
          ? "the live fetch did not answer · the mirror at left is local truth"
          : "fetching from Salesforce — not stored · the mirror at left is local truth"}
      </p>
    </div>
  )
}

function RecordSection({
  title,
  record,
}: {
  title: string
  record: Record_ | null
}) {
  return (
    <div className="border-tertiary flex flex-col gap-2 border-b py-3">
      <h3 className="text-primary text-sm">{title}</h3>
      {record ? (
        <KeyValues record={record} />
      ) : (
        <p className="text-quaternary text-sm">Not available.</p>
      )}
    </div>
  )
}

function RecordListSection({
  title,
  items,
}: {
  title: string
  items: Record_[]
}) {
  return (
    <div className="border-tertiary flex flex-col gap-2 border-b py-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-primary text-sm">{title}</h3>
        <span className="notation">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-quaternary text-sm">None.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((item, i) => (
            <li
              key={i}
              className="border-tertiary border-t pt-3 first:border-t-0 first:pt-0"
            >
              <KeyValues record={item} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function KeyValues({ record }: { record: Record_ }) {
  const entries = Object.entries(record).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  )
  if (entries.length === 0) return <p className="text-quaternary text-sm">—</p>
  return (
    <dl className="flex flex-col gap-1">
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between gap-3">
          <dt className="eyebrow tracking-[0.06em] normal-case">
            {humanize(key)}
          </dt>
          <dd className="text-secondary text-right text-xs">
            {display(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}
